import Stack from '@mui/material/Stack';
import { Col, Row } from 'antd';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GridBlockType } from 'services/entities/BlockTypes';
import useClickToAddTable from '../hooks/useClickToAddTable.ts';
import useClickToAddSimpleBlock from '../hooks/useClickToAddSimpleBlock';
import type { ClickToAddSimpleBlockConfig } from '../hooks/useClickToAddSimpleBlock';
import { shapeDefaultStyle } from '../hooks/useNewBlockSettings';
import { defaultBlockStyle } from '../hooks/useBlockSettings';
import ImageBlockButton from './ImageBlockButton.tsx';
import ShapeBlockButton from './ShapeBlockButton.tsx';
import './styles.less';
import TableBlockButton from './TableBlockButton';
import TextBlockButton from './textBlockButton';
import VideoBlockButton from './VideoBlockButton';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { FeatureFlags } from 'utils/featureFlags';
import EmbedBlockButton from './EmbedBlockButton.tsx';

const EMBED_CONFIG: ClickToAddSimpleBlockConfig = {
  type: GridBlockType.EMBED,
  content: {},
  clampWidth: true,
};

const VIDEO_CONFIG: ClickToAddSimpleBlockConfig = {
  type: GridBlockType.VIDEO,
  content: {},
  clampWidth: true,
};

const SHAPE_CONFIG: ClickToAddSimpleBlockConfig = {
  type: GridBlockType.SHAPE,
  content: { htmlContent: '', styles: shapeDefaultStyle },
  // Existing, pre-refactor behavior: unlike Embed/Video, Shape's default width is not clamped
  // to the available content-area width. Preserved as-is, not treated as a bug to fix here.
  clampWidth: false,
};

const TEXT_CONFIG: ClickToAddSimpleBlockConfig = {
  type: GridBlockType.TEXT,
  content: {
    htmlContent:
      '{"type":"doc","content":[{"type":"paragraph","attrs":{"lineHeight":null,"textAlign":null,"data-table-style":null,"data-style-set":null}}]}',
    styles: defaultBlockStyle,
  },
  clampWidth: true,
};

export function EditorToolbar() {
  const { t } = useTranslation();
  const { [FeatureFlags.sheetsExcelEmbed]: isSheetsExcelEmbedEnabled } = useFeatureFlag([FeatureFlags.sheetsExcelEmbed]);
  const clickToAddTable = useClickToAddTable();
  const clickToAddEmbed = useClickToAddSimpleBlock(EMBED_CONFIG);
  const clickToAddShape = useClickToAddSimpleBlock(SHAPE_CONFIG);
  const clickToAddVideo = useClickToAddSimpleBlock(VIDEO_CONFIG);
  const clickToAddText = useClickToAddSimpleBlock(TEXT_CONFIG);

  const imageBlockRef = useRef<HTMLDivElement | null>(null);

  return (
    <Stack direction="column" pb={6}>
      <div className="blocks">
        <Row className="blocks__row" gutter={16}>
          <Col data-testid="text-block-button" span={12}>
            <TextBlockButton title={t('editor.toolbar.text')} onClickAdd={() => clickToAddText()} />
          </Col>

          <Col data-testid="image-block-button" ref={imageBlockRef} span={12}>
            <ImageBlockButton />
          </Col>
        </Row>

        <Row className="blocks__row" gutter={16}>
          <Col data-testid="video-block-button" ref={imageBlockRef} span={12}>
            <VideoBlockButton title={t('editor.toolbar.video')} onClickAdd={() => clickToAddVideo()} />
          </Col>

          <Col data-testid="text-table-block-button" ref={imageBlockRef} span={12}>
            <TableBlockButton
              testId="text-table-block-button"
              title={t('document.table.text_table')}
              onClickAdd={() => clickToAddTable({ isPricingTable: false })}
            />
          </Col>
        </Row>

        <Row className="blocks__row" gutter={16}>
          <Col data-testid="shape-block-button" ref={imageBlockRef} span={12}>
            <ShapeBlockButton title={t('editor.toolbar.shape')} onClickAdd={() => clickToAddShape()} />
          </Col>
          <Col data-testid="pricing-table-block-button" ref={imageBlockRef} span={12}>
            <TableBlockButton
              testId="pricing-table-block-button"
              isPricingTable
              title={t('document.table.pricing_table')}
              onClickAdd={() => clickToAddTable({ isPricingTable: true })}
            />
          </Col>
        </Row>
        {isSheetsExcelEmbedEnabled && (
          <Row className="blocks__row" gutter={16}>
            <Col data-testid="embed-block-button" ref={imageBlockRef} span={12}>
              <EmbedBlockButton title={t('editor.toolbar.embed')} onClickAdd={() => clickToAddEmbed()} />
            </Col>
          </Row>
        )}
      </div>
    </Stack>
  );
}
